import { NotesPanelState } from '../../helpers/notesPanel'
import {
  ADD_NOTE_SUCCESS,
  CLEAR_NOTES_PANEL_FORCE,
  FORCE_OPEN_NOTES_PANEL,
  HIDE_NOTES_PANEL,
  REQUEST_NOTES_COMPOSER,
  SET_NOTES_PANEL_STATE,
  TOGGLE_NOTES,
  TOGGLE_NOTES_OFF,
  TOGGLE_NOTES_ON,
} from './panelActionTypes'

/**
 * The notes right-column's presentation state, split out of the (large) notes
 * reducer so it can be reasoned about — and tested — on its own.
 *
 * Note what is NOT here: `hideNotes`. That action clears the notes list and the
 * last list context for an Arc-write refetch; it is a data reset, not a visibility
 * control, and conflating the two would collapse the panel as a side effect of
 * refetching.
 */

/** Minimal action shape — avoids importing the api middleware's types. */
interface IPanelAction {
  type: string
  state?: NotesPanelState
  width?: number
  tab?: string | null
}

/**
 * Current effective state. `isNotesShown` is derived from this (=== 'docked') so
 * the ~15 existing consumers keep working while the model gains 'rail'/'hidden'.
 * Defaults to 'rail': cheap, and no content reflow before the resolver runs.
 */
export const notesPanelState = (
  state: NotesPanelState = NotesPanelState.rail,
  action: IPanelAction,
): NotesPanelState => {
  switch (action.type) {
    case SET_NOTES_PANEL_STATE:
      return action.state ?? state
    case TOGGLE_NOTES:
      return state === NotesPanelState.docked ? NotesPanelState.rail : NotesPanelState.docked
    case TOGGLE_NOTES_OFF:
      return NotesPanelState.rail
    case TOGGLE_NOTES_ON:
    case FORCE_OPEN_NOTES_PANEL:
      return NotesPanelState.docked
    case HIDE_NOTES_PANEL:
      return NotesPanelState.hidden
    default:
      return state
  }
}

/**
 * Latch that pins the panel open for a deep-link / flag jump, holding the tab that
 * jump asked for (Notes for cells/notes, Flags for a flag jump). A force-open always
 * names a tab, so this single field answers both "is it forced?" (non-null) and
 * "which tab?" — see the `notesPanelForced` selector.
 *
 * This is the whole reason `?noteId=` survives: NotesContainer strips that param
 * as soon as it selects the note, so a resolver deriving "is this a deep link?"
 * from the URL would re-collapse the panel while it is being read. The latch is
 * released by navigation or by an explicit user gesture — never by the strip.
 */
export const notesPanelForcedTab = (
  state: string | null = null,
  action: IPanelAction,
): string | null => {
  switch (action.type) {
    case FORCE_OPEN_NOTES_PANEL:
      return action.tab ?? null
    // The explicit user gestures below supersede a forced state, so the resolver
    // may take over again on the next navigation. Clearing the tab with the latch
    // matters: a tab left behind here re-selects itself whenever the available
    // tabs change, snapping back to a force-open the user already dismissed.
    case CLEAR_NOTES_PANEL_FORCE:
    case TOGGLE_NOTES:
    case TOGGLE_NOTES_OFF:
    case HIDE_NOTES_PANEL:
      return null
    default:
      return state
  }
}

/**
 * Set by the rail's quick-add so the docked panel focuses its composer instead of
 * merely appearing. Cleared once the note lands or the panel collapses, so a
 * later "+" focuses again.
 */
export const notesComposerRequested = (state = false, action: IPanelAction): boolean => {
  switch (action.type) {
    case REQUEST_NOTES_COMPOSER:
      return true
    case ADD_NOTE_SUCCESS:
    case TOGGLE_NOTES:
    case TOGGLE_NOTES_OFF:
    case HIDE_NOTES_PANEL:
      return false
    default:
      return state
  }
}
